/**
 * Cognito Authentication Service
 *
 * Handles all AWS Cognito authentication operations including:
 * - Email validation and tenant detection
 * - User authentication (password and IdP/SAML)
 * - User registration and email confirmation
 * - Password reset flows
 * - Session management via HttpOnly JWT cookie (set by backend)
 *
 * SECURITY: Tokens are never stored in localStorage. The backend sets a signed
 * JWT as an HttpOnly cookie on the shared domain. All API calls include
 * credentials: 'include' so the cookie is sent automatically.
 *
 * This service communicates with the Python backend API located at:
 * cognito_auth/api/auth_routes.py
 */

import i18n from "../i18n/i18n";

/**
 * Map backend error_code values to i18n translation keys.
 */
const ERROR_CODE_MAP = {
  incorrect_credentials: "errors.incorrectCredentials",
  user_not_confirmed: "errors.userNotConfirmed",
  username_exists: "errors.usernameExists",
  invalid_confirmation_code: "errors.invalidConfirmationCode",
  invalid_password: "errors.invalidPassword",
  token_expired: "errors.tokenExpired",
  pending_approval: "errors.pendingApproval",
  app_access_denied: "errors.accessDeniedApp",
  access_denied: "errors.accessDeniedGeneral",
  authentication_failed: "errors.authenticationFailed",
  registration_failed: "errors.registrationFailed",
  confirmation_failed: "errors.confirmationFailed",
  password_reset_failed: "errors.passwordResetFailed",
  email_check_failed: "errors.emailCheckFailed",
  client_validation_failed: "errors.clientValidationFailed",
  invalid_client_code: "errors.invalidClientCode",
  challenge_response_failed: "errors.challengeResponseFailed",
  failed_to_resend_code: "errors.failedToResendCode",
  invalid_tenant_client: "errors.invalidTenantClient",
  expired_confirmation_code: "errors.expiredConfirmationCode",
  already_confirmed: "errors.alreadyConfirmed",
  account_approval_failed: "errors.accountApprovalFailed",
  invalid_or_expired_token: "errors.invalidOrExpiredToken",
  sign_out_failed: "errors.signOutFailed",
  password_reset_required: "errors.passwordResetRequired",
  sso_user_password_reset: "errors.ssoUserPasswordReset",
};

/**
 * Translate a backend error response into the current UI language.
 * Falls back to the raw error message if no mapping exists.
 */
function translateError(errorData, fallbackMessage) {
  const code = errorData?.error_code;
  if (code && ERROR_CODE_MAP[code]) {
    return i18n.t(ERROR_CODE_MAP[code]);
  }
  return errorData?.error || fallbackMessage;
}

class CognitoAuthService {
  constructor() {
    this.baseUrl = process.env.REACT_APP_API_URL || "http://localhost:2021/api";
  }

  /**
   * Internal fetch wrapper.
   * Includes credentials so the HttpOnly auth cookie is sent automatically.
   */
  async _fetch(url, options = {}) {
    const defaultOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Accept-Language": i18n.language?.startsWith("fr") ? "fr" : "en",
      },
      credentials: "include",
      mode: "cors",
    };

    const mergedOptions = {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultOptions.headers,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, mergedOptions);
      return response;
    } catch (error) {
      console.error("Fetch error:", error);
      throw error;
    }
  }

  /**
   * Check if email exists and get tenant configuration
   * This is step 1 of the 2-step login flow
   */
  async checkEmail(email, { externalRedirect, appId } = {}) {
    try {
      const body = { email };
      if (externalRedirect) {
        body.external_redirect = externalRedirect;
      }
      if (appId) {
        body.app_id = appId;
      }
      const response = await this._fetch(`${this.baseUrl}/auth/check-email`, {
        method: "POST",
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(translateError(errorData, "Email check failed"));
      }

      const data = await response.json();

      return {
        userExists: data.exists,
        tenantId: data.tenant_id,
        clientId: data.client_id,
        hasIdp: data.has_idp || false,
        isIdpUser: data.is_idp_user || false,
        idpName: data.idp_name || "",
        idpUrl: data.idp_url || "",
        lang: data.lang || "en",
        userStatus: data.user_status || "",
      };
    } catch (error) {
      throw new Error(error.message || "Email check failed");
    }
  }

  /**
   * Validate a client code before showing the registration form
   */
  async validateClient(clientCode) {
    try {
      const response = await this._fetch(`${this.baseUrl}/auth/validate-client`, {
        method: "POST",
        body: JSON.stringify({ client_code: clientCode }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(translateError(errorData, "Invalid client code"));
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || "Client validation failed");
    }
  }

  async getIdpAuthorizeUrl(clientCode, { appId, externalRedirect } = {}) {
    try {
      const body = { client_code: clientCode };
      if (appId) body.app_id = appId;
      if (externalRedirect) body.external_redirect = externalRedirect;

      const response = await this._fetch(`${this.baseUrl}/auth/idp-authorize`, {
        method: "POST",
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(translateError(errorData, "Failed to get IdP URL"));
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || "Failed to get IdP URL");
    }
  }


  async getIdpAuthorizeUrl(clientCode, { appId, externalRedirect } = {}) {
    try {
      const body = { client_code: clientCode };
      if (appId) body.app_id = appId;
      if (externalRedirect) body.external_redirect = externalRedirect;

      const response = await this._fetch(`${this.baseUrl}/auth/idp-authorize`, {
        method: "POST",
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(translateError(errorData, "Failed to get IdP URL"));
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || "Failed to get IdP URL");
    }
  }

  /**
   * Sign in user with email and password.
   * The backend sets a JWT auth cookie on success — no client-side token storage.
   */
  async signIn(email, password, { appId } = {}) {
    try {
      const body = { email, password };
      if (appId) {
        body.app_id = appId;
      }
      const response = await this._fetch(`${this.baseUrl}/auth/signin`, {
        method: "POST",
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(translateError(errorData, "Sign in failed"));
      }

      const data = await response.json();

      // Check if additional challenge is required (e.g., MFA, NEW_PASSWORD_REQUIRED)
      if (data.challenge) {
        return {
          requiresChallenge: true,
          challenge: data.challenge,
          session: data.session,
          challengeParameters: data.challenge_parameters,
          tenantId: data.tenant_id,
        };
      }

      // Admin-initiated password reset — user has a code in their email
      if (data.password_reset_required) {
        return {
          passwordResetRequired: true,
          tenantId: data.tenant_id,
        };
      }

      return {
        success: true,
        ...data,
      };
    } catch (error) {
      throw new Error(error.message || "Sign in failed");
    }
  }

  /**
   * Respond to an authentication challenge (e.g., NEW_PASSWORD_REQUIRED).
   * On success the backend sets JWT auth cookies.
   */
  async respondToChallenge({ challengeName, session, username, tenantId, newPassword, appId }) {
    try {
      const body = {
        challenge_name: challengeName,
        session,
        username,
        tenant_id: tenantId,
      };
      if (newPassword) {
        body.new_password = newPassword;
      }
      if (appId) {
        body.app_id = appId;
      }
      const response = await this._fetch(`${this.baseUrl}/auth/respond-challenge`, {
        method: "POST",
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(translateError(errorData, "Challenge response failed"));
      }

      const data = await response.json();

      // Another challenge may follow (e.g., MFA)
      if (data.challenge) {
        return {
          requiresChallenge: true,
          challenge: data.challenge,
          session: data.session,
          challengeParameters: data.challenge_parameters,
          tenantId: data.tenant_id,
        };
      }

      return {
        success: true,
        ...data,
      };
    } catch (error) {
      throw new Error(error.message || "Challenge response failed");
    }
  }

  /**
   * Sign up new user
   */
  async signUp(clientCode, email, password, userAttributes = {}) {
    try {
      const response = await this._fetch(`${this.baseUrl}/auth/signup`, {
        method: "POST",
        body: JSON.stringify({
          client_code: clientCode,
          email,
          password,
          first_name: userAttributes.firstName,
          last_name: userAttributes.lastName,
          phone_number: userAttributes.phoneNumber,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(translateError(errorData, "Sign up failed"));
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || "Sign up failed");
    }
  }

  /**
   * Confirm sign up with verification code
   */
  async confirmSignUp(email, confirmationCode) {
    try {
      const body = {
        email,
        confirmation_code: confirmationCode,
      };
      const response = await this._fetch(`${this.baseUrl}/auth/confirm`, {
        method: "POST",
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(translateError(errorData, "Confirmation failed"));
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || "Confirmation failed");
    }
  }

  /**
   * Confirm sign up via emailed confirmation link (direct params, no email needed)
   */
  async confirmUserByLink({ tenantId, clientId, userName, confirmationCode, email }) {
    try {
      const body = {
        tenant_id: tenantId,
        client_id: clientId,
        user_name: userName,
        confirmation_code: confirmationCode,
      };
      if (email) {
        body.email = email;
      }
      const response = await this._fetch(`${this.baseUrl}/auth/confirm-link`, {
        method: "POST",
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(translateError(errorData, "Confirmation failed"));
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || "Confirmation failed");
    }
  }

  /**
   * Resend confirmation code
   */
  async resendConfirmationCode(email) {
    try {
      const body = { email };
      const response = await this._fetch(`${this.baseUrl}/auth/resend-code`, {
        method: "POST",
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(translateError(errorData, "Failed to resend code"));
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || "Failed to resend code");
    }
  }

  /**
   * Initiate forgot password flow
   */
  async forgotPassword(email) {
    try {
      const response = await this._fetch(
        `${this.baseUrl}/auth/forgot-password`,
        {
          method: "POST",
          body: JSON.stringify({ email }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(translateError(errorData, "Failed to initiate password reset"));
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || "Failed to initiate password reset");
    }
  }

  /**
   * Confirm forgot password with new password
   */
  async confirmForgotPassword(email, confirmationCode, newPassword) {
    try {
      const response = await this._fetch(
        `${this.baseUrl}/auth/confirm-password`,
        {
          method: "POST",
          body: JSON.stringify({
            email,
            confirmation_code: confirmationCode,
            new_password: newPassword,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(translateError(errorData, "Password reset failed"));
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || "Password reset failed");
    }
  }

  /**
   * Sign out user — backend clears the auth cookie
   */
  async signOut() {
    try {
      await this._fetch(`${this.baseUrl}/auth/signout`, {
        method: "POST",
      });
    } catch (error) {
      console.warn("Sign out API call failed:", error);
    }
  }

  /**
   * Check if user is authenticated via backend session-check (reads JWT cookie).
   * When appId is provided, also checks entitlement for that application.
   *
   * Returns:
   *   { authenticated: false } — no valid session
   *   { authenticated: true, entitled: true }  — valid session, entitled to app
   *   { authenticated: true, entitled: false } — valid session, NOT entitled
   *   { authenticated: true } — valid session, no appId requested
   */
  async isAuthenticated(appId = "") {
    try {
      let url = `${this.baseUrl}/auth/session-check`;
      if (appId) {
        url += `?app_id=${encodeURIComponent(appId)}`;
      }
      const response = await this._fetch(url, {
        method: "GET",
      });
      if (!response.ok) {
        return { authenticated: false };
      }

      const contentType = response.headers.get("content-type") || "";
      if (!contentType.includes("application/json")) {
        return { authenticated: false };
      }

      const data = await response.json();
      if (!data || data.authenticated !== true) {
        return { authenticated: false };
      }

      return {
        authenticated: true,
        entitled: data.entitled,
      };
    } catch (error) {
      return { authenticated: false };
    }
  }

  /**
   * Get approval info for a pending account (admin approval page)
   */
  async getApprovalInfo(token) {
    try {
      const response = await this._fetch(
        `${this.baseUrl}/auth/approval-info?token=${encodeURIComponent(token)}`,
        { method: "GET" },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(translateError(errorData, "Failed to get approval info"));
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || "Failed to get approval info");
    }
  }

  /**
   * Approve a user account (admin action)
   */
  async approveAccount(token) {
    try {
      const response = await this._fetch(
        `${this.baseUrl}/auth/approve-account`,
        {
          method: "POST",
          body: JSON.stringify({ token }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(translateError(errorData, "Account approval failed"));
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || "Account approval failed");
    }
  }

}

// Export singleton instance
const cognitoAuthService = new CognitoAuthService();
export default cognitoAuthService;
